const ADMIN_IDLE_TIMEOUT_MS = 10 * 60 * 1000
const ADMIN_ACTIVITY_THROTTLE_MS = 15 * 1000
const ADMIN_LAST_ACTIVITY_KEY = "adminLastActivityAt"

const isBrowser = () => typeof window !== "undefined" && typeof localStorage !== "undefined"

const decodeJwtPayload = (token) => {
  try {
    if (!token || typeof token !== "string") return null
    const parts = token.split(".")
    if (parts.length < 2) return null
    const base64Url = parts[1]
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/")
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4)
    const json = atob(padded)
    return JSON.parse(json)
  } catch (_) {
    return null
  }
}

export const isAdminSessionToken = (token) => {
  const payload = decodeJwtPayload(token)
  return String(payload?.role || "") === "A"
}

const getToken = () => {
  if (!isBrowser()) return ""
  return localStorage.getItem("token") || ""
}

const getLastActivity = () => {
  if (!isBrowser()) return NaN
  const value = Number(localStorage.getItem(ADMIN_LAST_ACTIVITY_KEY))
  return Number.isFinite(value) ? value : NaN
}

const setLastActivity = (timeMs = Date.now()) => {
  if (!isBrowser()) return
  localStorage.setItem(ADMIN_LAST_ACTIVITY_KEY, String(timeMs))
}

const clearLastActivity = () => {
  if (!isBrowser()) return
  localStorage.removeItem(ADMIN_LAST_ACTIVITY_KEY)
}

const normalizeAppPath = (path = "/") => {
  const value = String(path || "/").trim() || "/"
  return value.startsWith("/") ? value : `/${value}`
}

export const getCurrentAppPath = () => {
  if (!isBrowser()) return "/"
  return normalizeAppPath(window.location.pathname || "/")
}

export const buildAppUrl = (path = "/") => {
  return normalizeAppPath(path)
}

export const redirectToAppPath = (path = "/") => {
  if (!isBrowser()) return
  window.location.replace(buildAppUrl(path))
}

export const clearAuthAndAdminSession = () => {
  if (!isBrowser()) return
  localStorage.removeItem("token")
  clearLastActivity()
}

export const initializeAdminSessionForToken = (token) => {
  if (!isBrowser()) return
  if (isAdminSessionToken(token)) {
    setLastActivity(Date.now())
  } else {
    clearLastActivity()
  }
}

export const touchAdminSessionActivity = (nowMs = Date.now()) => {
  const token = getToken()
  if (!isAdminSessionToken(token)) {
    clearLastActivity()
    return false
  }

  const last = getLastActivity()
  if (!Number.isFinite(last) || nowMs - last >= ADMIN_ACTIVITY_THROTTLE_MS) {
    setLastActivity(nowMs)
    return true
  }

  return false
}

export const isAdminSessionIdleExpired = (nowMs = Date.now()) => {
  const token = getToken()
  if (!isAdminSessionToken(token)) return false

  const last = getLastActivity()
  if (!Number.isFinite(last) || last <= 0) return true
  return nowMs - last >= ADMIN_IDLE_TIMEOUT_MS
}

export const enforceAdminSessionTimeout = (nowMs = Date.now()) => {
  if (!isAdminSessionIdleExpired(nowMs)) return false
  clearAuthAndAdminSession()
  return true
}

export { ADMIN_IDLE_TIMEOUT_MS }
